import { PublicBrandShell } from '../../components/public/PublicBranding';
import { IN_APP_FOOTER_LINKS } from './publicFooterLinks';

// Mirrored from kolega.dev/terms (KLG Tech Innovations Limited), kept in sync with
// ../kolegadevwebsite/src/app/terms. NOTE: §5.3 Payment Terms was updated CityPay -> Paddle
// (Merchant of Record) for the Paddle migration — legal wording flagged for sign-off.
export default function TermsPage() {
  return (
    <PublicBrandShell footerLinks={IN_APP_FOOTER_LINKS}>
      <div className="min-h-screen bg-[#0B0E08] text-[#A8AE9E]">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h1 className="text-3xl font-bold text-gray-200 mb-2">Terms of Service</h1>
          <p className="text-xs text-[#7E8473] mb-8">Last Updated: May 8, 2026</p>

          <div className="space-y-6 text-sm">
            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">1. Introduction and Acceptance</h3>
              <p className="mb-3">
                Welcome to Kolega.Dev, operated by KLG Tech Innovations Limited (&quot;Kolega,&quot; &quot;we,&quot; &quot;us&quot;).
                These Terms of Service (&quot;Terms&quot;) govern your use of our autonomous security remediation
                platform and services at kolega.dev (the &quot;Service&quot;).
              </p>
              <p>
                This policy applies to individual users of our Service for the Free and Pro tiers. By creating
                an account, accessing our platform, or using our Service, you agree to these Terms, our Privacy
                Policy (kolega.dev/privacy-policy), our Copyright Notice (kolega.dev/copyright-notice), and all
                applicable laws. If you&apos;re using our Service for an organisation, you represent that you have
                authority to bind that organisation to these Terms.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">2. Service Description</h3>
              <p>
                Kolega.Dev is an autonomous security remediation platform that connects to your GitHub repositories
                via GitHub App. The Service detects vulnerabilities through traditional and AI-powered scans, generates
                context-aware fixes, tests them in isolated environments, and creates production-ready pull requests
                automatically. Our Service includes code scanning (SAST, SCA, Secret Detection), AI-powered deep
                analysis, automated remediation generation, and integrations with development tools such as GitHub and Jira.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">3. Eligibility</h3>
              <p className="mb-3">
                You must be at least 18 years old to use our Service. By registering, you confirm that:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>You meet the age requirement</li>
                <li>You haven&apos;t been previously banned from our Service</li>
                <li>Your use complies with all applicable laws</li>
                <li>You have authority to accept these Terms</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">4. Account Registration and Security</h3>
              <p className="mb-3">
                To access our Service, you must create an account using GitHub OAuth authentication with accurate,
                complete information. You&apos;re responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Maintaining current account information</li>
                <li>Protecting your login credentials and GitHub access tokens</li>
                <li>All activities under your account</li>
                <li>Notifying us immediately of unauthorised access at contact@kolega.ai</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">5. Subscription Plans and Pricing</h3>

              <h4 className="text-md font-medium text-gray-200 mb-2">5.1 Service Tiers</h4>
              <p className="mb-3">We offer the following subscription plans:</p>

              <div className="space-y-3">
                <div>
                  <h5 className="font-medium text-gray-200">Free ($0/month):</h5>
                  <p>1 application, 250k lines of code per application, 0 PRs for private repositories,
                  scheduled scanning only with 20 SAST scans/month and 4 deep scans/month.
                  Includes limited core features: issue context and estimated time,
                  and ticket integration.</p>
                </div>

                <div>
                  <h5 className="font-medium text-gray-200">Starter ($39/month):</h5>
                  <p>1 application, 200k lines of code per application, 40 PRs/month for private repositories,
                  scheduled scanning only with 20 SAST scans/month and 4 deep scans/month, noise reduction,
                  automated fixes, and ticket integration. Does not include compliance modules, audit logging,
                  or enterprise features.</p>
                </div>

                <div>
                  <h5 className="font-medium text-gray-200">Pro ($99/month):</h5>
                  <p>1 application, 250k lines of code per application, 100 PRs/month for private repositories,
                  scheduled scanning only with 20 SAST scans/month and 4 deep scans/month, noise reduction,
                  LOC top-ups, and compliance readiness. Includes all core features with fixed limits.</p>
                </div>

                <div>
                  <h5 className="font-medium text-gray-200">Team ($499/month):</h5>
                  <p>Up to 5 applications, 500k lines of code per application, 400 PRs/month for private repositories,
                  on-demand and triggered scanning with 80 SAST scans/month and 16 deep scans/month, noise reduction,
                  LOC top-ups, flexible top-ups for scans and PRs, compliance readiness, and action audit and logging.
                  Includes all core features.</p>
                </div>

                <div>
                  <h5 className="font-medium text-gray-200">Enterprise (Custom pricing):</h5>
                  <p>Custom applications, custom lines of code, custom PRs for all repositories,
                  custom/continuous scanning frequency with custom scan allowances.
                  Includes all core features plus enterprise features: action audit and logging,
                  self-hosted runners, SSO/SAML authentication, automated vulnerability exploitation testing,
                  and comprehensive compliance readiness (SOC2, ISO, HIPAA, GDPR, CCPA, PCI, Bespoke).</p>
                </div>
              </div>

              <p className="mt-3">
                Additional scan and PR credits are available as top-up options for Team tier subscribers.
                Top-up credits allow you to extend your usage beyond the included monthly allowances.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2 mt-4">5.2 Pricing and Currency</h4>
              <p className="mb-3">
                All fees are displayed in USD and are non-refundable except where required by law.
                Prices may be shown in other currencies based on your location at current exchange rates.
                Taxes may apply and will be calculated at checkout.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2">5.3 Payment Terms</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Monthly subscriptions auto-renew unless cancelled before the renewal date</li>
                <li>
                  Payment processing is handled by Paddle, who acts as the Merchant of Record (MoR) for every
                  transaction. Paddle (not Kolega) is the seller of record, appears on your billing statement, and is
                  responsible for payment processing, invoicing, and global sales tax, VAT, and GST. See our{' '}
                  <a href="/refund" className="text-[#ADDD30] hover:underline">Refund Policy</a> for cancellation and
                  refund details.
                </li>
                <li>You authorise Paddle to charge your payment method for all applicable fees</li>
                <li>Unpaid accounts may be suspended or terminated</li>
              </ul>

              <h4 className="text-md font-medium text-gray-200 mb-2 mt-4">5.4 Usage Limits</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Subscriptions provide platform access, features, and monthly PR allowances</li>
                <li>PR limits for private repositories reset monthly based on your billing cycle</li>
                <li>Unused PRs do not roll over to the following month</li>
              </ul>

              <h4 className="text-md font-medium text-gray-200 mb-2 mt-4">5.5 Upgrades and Overages</h4>
              <p>
                If you exceed your monthly PR limits for private repositories, you may upgrade to a higher tier
                to continue generating pull requests. Team tier subscribers can purchase top-up credits for additional
                scans and PRs. Enterprise customers may contact us for custom usage arrangements.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">6. Acceptable Use Policy</h3>

              <h4 className="text-md font-medium text-gray-200 mb-2">6.1 Prohibited Activities</h4>
              <p className="mb-3">You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Infringe intellectual property rights</li>
                <li>Violate any laws or regulations</li>
                <li>Harass, threaten, or harm others</li>
                <li>Distribute malware or harmful code</li>
                <li>Attempt unauthorised access to systems</li>
                <li>Scrape or extract data from our Service</li>
                <li>Interfere with Service operations</li>
                <li>Use the Service to develop competing products</li>
                <li>Submit sensitive regulated data (HIPAA, PCI-DSS, etc.)</li>
                <li>Use the Service to scan repositories you do not have authorisation to access</li>
                <li>Abuse the remediation features to generate code for purposes other than security fixes</li>
                <li>Impersonate others or misrepresent affiliations</li>
              </ul>

              <h4 className="text-md font-medium text-gray-200 mb-2 mt-4">6.2 Content Standards</h4>
              <p>
                All code and content you submit for scanning must comply with applicable laws and must not
                contain material that is offensive, discriminatory, or harmful.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2 mt-4">6.3 Enforcement</h4>
              <p>
                Violations may result in warnings, suspension, or permanent termination at our discretion.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">7. Intellectual Property Rights</h3>

              <h4 className="text-md font-medium text-gray-200 mb-2">7.1 Our Property</h4>
              <p className="mb-3">
                The Service, including its design, features, and technology, remains our exclusive property.
                No rights are granted except those explicitly stated in these Terms.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2">7.2 Your Content</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>You retain ownership of code you provide for scanning (&quot;Input&quot;)</li>
                <li>You own the remediation patches and fixes Kolega.Dev generates for you (&quot;Output&quot;)</li>
                <li>You grant us a limited licence to process your Input solely to provide the Service</li>
                <li>Similar Outputs may be generated for other users with similar vulnerabilities</li>
              </ul>

              <h4 className="text-md font-medium text-gray-200 mb-2 mt-4">7.3 No Training Use</h4>
              <p>We do not use your Input or Output to train AI models.</p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">8. Beta Features</h3>
              <p>
                We may offer trial or beta features that are provided &quot;AS IS&quot; without warranties.
                These may be modified or discontinued at any time. Your use of beta features
                constitutes acceptance of these conditions.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">9. Third-Party Services</h3>
              <p>
                Our Service integrates with third-party platforms including GitHub for repository access
                and pull request creation, Jira for ticket management, and AI providers (Anthropic and OpenAI)
                for code analysis and fix generation. Your use of these integrations is subject to their
                respective terms. We&apos;re not responsible for third-party services&apos; functionality or data practices.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">10. Privacy and Data Protection</h3>
              <p className="mb-3">
                Our Privacy Policy explains how we collect and use your information. Key points include:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>We process data only to provide our Service</li>
                <li>Code is accessed temporarily for scanning and discarded after processing</li>
                <li>We implement industry-standard security measures</li>
                <li>Your data is not used for AI training</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">11. Service Modifications</h3>
              <p>
                We may modify, suspend, or discontinue Service features at any time. While we strive for
                reliability, we cannot guarantee uninterrupted access. You should maintain your own backups
                of important code and configurations.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">12. Indemnification</h3>
              <p className="mb-3">
                You agree to defend and indemnify Kolega and its affiliates from any claims arising from:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your use of the Service</li>
                <li>Violations of these Terms</li>
                <li>Infringement of third-party rights</li>
                <li>Disputes with other parties</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">13. Disclaimers</h3>
              <p className="font-medium">
                THE SERVICE IS PROVIDED &quot;AS IS&quot; WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL
                IMPLIED WARRANTIES INCLUDING MERCHANTABILITY AND FITNESS FOR PARTICULAR PURPOSE.
                GENERATED REMEDIATIONS AND FIXES MAY CONTAIN ERRORS AND SHOULD BE REVIEWED BEFORE
                MERGING TO PRODUCTION. KOLEGA.DEV DOES NOT GUARANTEE THAT ALL VULNERABILITIES WILL
                BE DETECTED OR THAT ALL FIXES WILL BE CORRECT.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">14. Limitation of Liability</h3>
              <p className="mb-3 font-medium">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>We exclude liability for indirect, consequential, or punitive damages</li>
                <li>Our total liability is limited to fees paid in the 12 months before the claim or &euro;100, whichever is greater</li>
                <li>These limitations are fundamental to our Service offering</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">15. Termination</h3>

              <h4 className="text-md font-medium text-gray-200 mb-2">15.1 Termination Rights</h4>
              <ul className="list-disc pl-6 space-y-1 mb-3">
                <li>You may stop using our Service at any time</li>
                <li>We may terminate accounts for violations or at our discretion</li>
                <li>Termination doesn&apos;t relieve payment obligations</li>
              </ul>

              <h4 className="text-md font-medium text-gray-200 mb-2">15.2 Effect of Termination</h4>
              <p className="mb-3">Upon termination:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Your access ends immediately</li>
                <li>Outstanding fees remain due</li>
                <li>You may lose access to scan results and remediation history</li>
                <li>Certain Terms survive (intellectual property, indemnification, limitations)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">16. Dispute Resolution</h3>

              <h4 className="text-md font-medium text-gray-200 mb-2">16.1 Governing Law</h4>
              <p className="mb-3">
                These Terms are governed by UK law. The courts of the United Kingdom have exclusive jurisdiction.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2">16.2 Dispute Process</h4>
              <p>
                We encourage resolving disputes through direct communication. Contact us at contact@kolega.ai
                to address concerns before pursuing formal proceedings.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">17. General Provisions</h3>

              <h4 className="text-md font-medium text-gray-200 mb-2">17.1 Complete Agreement</h4>
              <p className="mb-3">These Terms and referenced policies constitute our entire agreement.</p>

              <h4 className="text-md font-medium text-gray-200 mb-2">17.2 Updates</h4>
              <p className="mb-3">
                We may update these Terms with 30 days&apos; notice for material changes. Continued use constitutes acceptance.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2">17.3 Assignment</h4>
              <p className="mb-3">
                You may not transfer these Terms. We may assign our rights and obligations.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2">17.4 Electronic Communications</h4>
              <p>
                You consent to receive electronic communications which satisfy legal writing requirements.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">18. Contact Information</h3>
              <div className="bg-[#21281A]/50 p-4 rounded-lg">
                <p className="font-medium text-gray-200">KLG Tech Innovations Limited</p>
                <p>Val Verclut, La Route des Cotils</p>
                <p>Grouville, JE3 9AP, Jersey</p>
                <p>Email: contact@kolega.ai</p>
              </div>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-200 mb-3">19. Additional Provisions</h3>

              <h4 className="text-md font-medium text-gray-200 mb-2">19.1 Export Compliance</h4>
              <p className="mb-3">
                The Service may be subject to export controls. You agree to comply with applicable trade regulations.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2">19.2 Force Majeure</h4>
              <p className="mb-3">
                Neither party is liable for delays due to circumstances beyond reasonable control.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2">19.3 Questions and Contact</h4>
              <p className="mb-3">
                All users may contact us with questions about these Terms or our Service using the contact information provided above.
              </p>

              <h4 className="text-md font-medium text-gray-200 mb-2">19.4 Support</h4>
              <p>We provide support at our discretion according to published policies.</p>
            </section>

            <div className="border-t border-[#2C3420] pt-4 text-center text-xs text-[#7E8473]">
              &copy; {new Date().getFullYear()} Kolega.Dev. All rights reserved.
            </div>
          </div>
        </main>
      </div>
    </PublicBrandShell>
  );
}
